import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  Input,
  OnChanges,
  SimpleChanges,
  Inject,
  ChangeDetectorRef, OnDestroy
} from '@angular/core';
import { IclinicalTrials } from '../../interfaces/clinical-trials.interface';
import { DynamicModalLoaderService } from '@ispm/layout/services/dynamic-modal-loader.service';
import { TitleWithModal } from '../../classes/TitleWithModal';
import { Workflow } from '@ispm/core/interfaces/workflow.interface';
import { BasicModalComponent } from '@ispm/layout/components/modals/basic-modal/basic-modal.component';
import { TherapiesService } from '../../services/therapies.service';
import { TherapiesActionCreatorService } from '../../services/therapies-action-creator.service';
import { Idrug } from '../../interfaces/drug.interface';
import { PermissionSchemaConstants } from '@ispm/shared/constants/permission-schema.constants';
import { get } from 'lodash';
import { Subject } from 'rxjs';
import { PlaceholderModalComponent } from '@ispm/layout/components/modals/placeholder-modal/placeholder-modal.component';
import { PlaceholderModalLoaderPayload } from '@ispm/layout/classes/placeholder-modal-loader-payload';
import { EditTherapyModalComponent } from '@ispm/therapies/components/edit-therapy-modal/edit-therapy-modal.component';
import { TherapyEditPayload } from '@ispm/therapies/classes/therapy-edit-payload.class';
import { ModalTypes } from '@ispm/layout/components/modals/contants/modal-types.enum';
import { take, takeUntil } from 'rxjs/operators';
import { modalResponse } from '@ispm/layout/constants/modal-responses.enum';
import { TestService } from '@ispm/dashboard/test/services/test.service';
import { AuthenticationService } from '@ispm/user/services/authentication.service';

@Component({
  selector: 'gen-therapy-card',
  templateUrl: './therapy-card.component.html',
  styleUrls: ['./therapy-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TherapyCardComponent implements OnInit, OnDestroy, OnChanges {
  @Input() trials: IclinicalTrials[];

  @Input() therapy: any;
  @Input() opinion: any;
  @Input() uncharacterizedTitle: string;
  @Input() uncharacterizedIconClass: string;
  @Input() caseStatus: boolean;
  @Input() revisionId: string;
  @Input() workflowInputs: any[];
  @Input() activeJobName: any;
  @Input() isWildTypeTherapyMatch: boolean;
  @Input() drugsList: any;
  public checkboxDisabled: boolean = false;
  private workflowPermissionWritable: boolean = false;
  public visibleInReport: boolean;
  public therapyIdentifiersList: string[];
  public includeInReportLabel: string = 'Include in report';
  public show: boolean = false;
  public titles: any[];
  public drugsObject: any;
  public therapyMatchEditConfiguration = get(this.testService.testConfiguration, 'therapyEditing.forms', null);


  public isTherapyUncurated: boolean = false;
  public isIncludeInReportVisible: boolean = false;
  public isTestCompleted: boolean = false;
  private destroyer: Subject<null> = new Subject<null>();

  constructor(private testService : TestService,
              private authService: AuthenticationService,
              private ModalLoader: DynamicModalLoaderService,
              private therapyService: TherapiesService,
              private _changeDetector: ChangeDetectorRef,
              private therapyAC: TherapiesActionCreatorService) {

  }

  ngOnInit() {
    // get if current user has write permissions for this view
    this.workflowPermissionWritable = this.authService.doesUserHasPermission(PermissionSchemaConstants.schemaWorkflowWrite);
    this.init();
  }
  init() {
    this.titles = [
      new TitleWithModal('Gene', this.therapy.gene.name, 'Gene Role', this.therapy.gene.roleInCancer, '', 'TherapyMatches_Table_gene_name'),
      new TitleWithModal('Aberration', this.therapy.alteration, 'Aberration Effect', this.therapy.effect, '', 'TherapyMatches_Table_Item_aberration'),
      new TitleWithModal('Sequence Change', this.therapy.codingSeqChange, 'Aberration Effect', this.therapy.effect, '', 'TherapyMatches_Table_Item_sequence')
    ];

    this.updateIndications();

    // get if current user has write permissions for this view
    this.workflowPermissionWritable = this.authService.doesUserHasPermission(PermissionSchemaConstants.schemaWorkflowWrite);

    this.therapy.drugs = this.therapy.drugs.filter(drug => !(!drug.fdaApprovedForIndication && !drug.investigationalForIndication && !drug.fdaApprovedOutsideIndication));
    this.sortDrugsByApprovalLevel();

    this.isTherapyUncurated = this.therapy.type === 'uncurated';

    this.isIncludeInReportVisible = this.testService.testConfiguration &&
      this.testService.testConfiguration.xHtmlReportTemplates &&
      this.testService.testConfiguration.xHtmlReportTemplates.length > 0;

    this.isTestCompleted = this.activeJobName.toLowerCase() === Workflow.COMPLETED_STEP_NAME.toLowerCase();

    this.checkboxDisabled = this.checkStateShouldDisableToggle();

    this.visibleInReport = this.isVisibleInReport(this.opinion[0]);

    this.therapyIdentifiersList = [];

    for (const type in this.drugsObject) {
      if (this.drugsObject.hasOwnProperty(type)) {
        this.drugsObject[type]['list'].map((drug) => {
          this.therapyIdentifiersList.push(this.getIdentifier(drug, type));
        });
      }
    }

    if (this.drugsList && this.drugsList != undefined) {
      this.checkDrugs(this.drugsList);
    }

  }


  ngOnChanges(changes: SimpleChanges): void {
    if ((changes.workflowInputs && !changes.workflowInputs.firstChange && !!changes.workflowInputs.currentValue)) {
      this.updateIndications();
    }
  }

  checkStateShouldDisableToggle() {

    // if current user does not have write permissions, return to disable action
    if (this.workflowPermissionWritable === false) {
      return true;
    }

    let shouldDisable: boolean = false;
    this.activeJobName.toLowerCase() === 'final review' || this.activeJobName.toLowerCase() === 'uimanualaberrationcuration' || this.activeJobName.toLowerCase() === 'interpretationresults'
      ? shouldDisable = false
      : shouldDisable = true;
    return shouldDisable;
  }

  updateIndications() {
    let selectionIndication = { };
    const input = Workflow.getInput(this.workflowInputs, 'selection');

    try {
      selectionIndication = JSON.parse(input['value']);
    } catch (e) {
      selectionIndication = { };
    }

    for (const indication in selectionIndication) {
      if (this.drugsObject.hasOwnProperty(indication)) {
        this.drugsObject[indication].setSelectable(selectionIndication[indication]);
      }
    }
  }

  sortDrugsByApprovalLevel() {
    for (let drug of this.therapy.drugs) {
      drug.aberration = this.therapy.alteration;
      drug.gene = this.therapy.gene.name;
      if (drug['fdaApprovedForIndication']) {
        drug = { ...drug};
        this.drugsObject['within'].addDrug(drug);
      }
      if (drug['fdaApprovedOutsideIndication']) {
        drug = { ...drug};
        this.drugsObject['outside'].addDrug(drug);
      }

      if (drug['investigationalForIndication']) {
        drug = { ...drug};
        this.drugsObject['investigation'].addDrug(drug);
      }

    }
  }

  isVisibleInReport(opinion) {
    if (opinion.hasOwnProperty('includeInReport')) {
      return opinion['includeInReport'].hasOwnProperty(this.therapy.id)
        ? opinion['includeInReport'][this.therapy.id]['visible']
        : true;
    } else {
      return true;
    }
  }

  toggleSelection(event) {
   if (this.visibleInReport === event) {
     return;
   }

   if (this.checkboxDisabled) {
     return;
   }

   this.visibleInReport = event;
   this.therapyAC.updateInclusionOpinion('report', this.therapy.id, event)
     .pipe(take(1))
     .subscribe((res: any) => {
       this.visibleInReport = this.isVisibleInReport(res);
     });
  }

  toggleCard() {
    this.show = !this.show;
  }

  openModal(title, content) {
    this.ModalLoader.popupModal(BasicModalComponent, { title, content});
  }

  checkDrugs(event) {
    if (event.some((drug) => this.therapyIdentifiersList.includes(drug))) {
      this.toggleSelection(true);
      this.checkboxDisabled = true;
    } else {
      this.checkboxDisabled = this.checkStateShouldDisableToggle();
    }

  }

  getIdentifier(drugObject: Idrug, source: string) {
    drugObject['source'] = source;
    return TherapiesActionCreatorService.getTherapyIdentifier(drugObject);
  }

  openEditTherapy() {
    this.ModalLoader.popupModal(PlaceholderModalComponent,
      new PlaceholderModalLoaderPayload(
        'Edit therapies for ' + [this.therapy.gene.name, this.therapy.alteration].join(' '),
        EditTherapyModalComponent,
        'Save',
        'Cancel',
        new TherapyEditPayload(this.therapy, this.testService.testConfiguration.id, this.therapyMatchEditConfiguration)
      ), ModalTypes.NONE, 'THERAPY-MATCH'
    )
      .pipe(
        take(1),
        takeUntil(this.destroyer.asObservable())
      )
      .subscribe((response: any) => {
        switch (response.payload.data) {
          case modalResponse.approve:
            this.therapy = response.payload.payload.therapy;
            this.init();
            this._changeDetector.markForCheck();
            this._changeDetector.detectChanges();
            break;
        }
      });
  }
  ngOnDestroy(): void {
    this.destroyer.next();
    this.destroyer.complete();
  }

}
